import { useCallback, useEffect, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Button, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router';
import { getActiveProviders } from '../../../store/provider.slice';
import { fetchPopulations, fetchAnalyticPopulations } from '../../../store/population.slice';
import Authorization from '../../../utils/Authorization';
import { getCustomDashboardTabs, setIsWidgetExisting } from '../../../store/smartDashboard.slice';
import HedisMetricSummary from './HedisMetricSummary';
import QAMetricSummary from './QAMetricSummary';
import DashRTCS from '../../ModelRTCS/DashRTCS';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { useHedisMetricsContext } from '../../../hooks/hedisMetricsContext';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';

const MetricSummary = (props) => {
    const auth = new Authorization();
    const history = useNavigate();
    const dispatch = useAppDispatch();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [providerTeam, setProviderTeam] = useState('All');

    const {
        updateAuthData,

        selectedProvider,
        showInActiveProviders,

        setAppointmentSelectedProvider,
        setAppointmentShowInActiveProviders,

        setEngagementSelectedTeam,
        setEngagementShowInActiveTeams,

        setIndividualEngagementSelectedProvider,
        setIndividualEngagementShowInActiveProviders,

        setTcTeam,
        setTcShowInActiveTeams,
    } = useWidgetContext();

    const {
        selectedTeam,
        showInActiveTeams,

        setAdultHealthTeam,
        setAdultHealthIncludePastOpenStatus,
        setAdultHealthShowInActiveTeam,

        setBpTeam,
        setBpIncludePastOpenStatus,
        setBpShowInActiveTeam,

        setBmiTeam,
        setBmiIncludePastOpenStatus,
        setBmiShowInActiveTeam,

        setHgba1cTeam,
        setHgba1cIncludePastOpenStatus,
        setHgba1cShowInActiveTeam,

        setDiabeticEyeExamTeam,
        setDiabeticEyeExamIncludePastOpenStatus,
        setDiabeticEyeExamShowInActiveTeam,

        setMedicationReconciliationTeam,
        setMedicationReconciliationIncludePastOpenStatus,
        setMedicationReconciliationShowInActiveTeam,

        setDepressionScreeningTeam,
        setDepressionScreeningIncludePastOpenStatus,
        setDepressionScreeningShowInActiveTeam,

        setDepressionFollowUpTeam,
        setDepressionFollowUpIncludePastOpenStatus,
        setDepressionFollowUpShowInActiveTeam,

        setBhDiagnosticTeam,
        setBhDiagnosticIncludePastOpenStatus,
        setBhDiagnosticShowInActiveTeam,

        setHgbA1cForBhDxLoadedTeam,
        setHgbA1cForBhDxIncludePastOpenStatus,
        setHgbA1cForBhDxShowInActiveTeam,

        setLipidForBhDxLoadedTeam,
        setLipidForBhDxIncludePastOpenStatus,
        setLipidForBhDxShowInActiveTeam,

        setCervicalCancerScreeningTeam,
        setCervicalCancerScreeningIncludePastOpenStatus,
        setCervicalCancerScreeningShowInActiveTeam,

        setColorectalCancerScreeningTeam,
        setColorectalCancerScreeningIncludePastOpenStatus,
        setColorectalCancerScreeningShowInActiveTeam,
    } = useHedisMetricsContext();

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
        dispatch(setIsWidgetExisting(true));
        setIsFetched(true);
    }, [isFetched, setIsFetched, dispatch]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        dispatch(getCustomDashboardTabs(auth.UserId, auth.Role));
        dispatch(getActiveProviders(true));
        dispatch(fetchPopulations());
        dispatch(fetchAnalyticPopulations());
        document.title = props.title;
        // Update auth data in context after login
        updateAuthData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (Boolean(selectedProvider)) {
            const selectedTeamInfo = teams.find((t) => t.TeamRoles.some((role) => role.AssignedProviderId === selectedProvider));
            if (Boolean(selectedTeamInfo)) {
                setProviderTeam(selectedTeamInfo.Id);
            }
        }
    }, [selectedProvider, teams]);

    const navigateToSmartDashboard = () => {
        setAppointmentSelectedProvider(selectedProvider);
        setAppointmentShowInActiveProviders(showInActiveProviders);

        setEngagementSelectedTeam(providerTeam);
        setEngagementShowInActiveTeams(showInActiveProviders);

        setIndividualEngagementSelectedProvider(selectedProvider);
        setIndividualEngagementShowInActiveProviders(showInActiveProviders);

        setHgba1cTeam(selectedTeam);
        setHgba1cIncludePastOpenStatus(true);
        setHgba1cShowInActiveTeam(showInActiveTeams);

        setDiabeticEyeExamTeam(selectedTeam);
        setDiabeticEyeExamIncludePastOpenStatus(true);
        setDiabeticEyeExamShowInActiveTeam(showInActiveTeams);

        setBpTeam(selectedTeam);
        setBpIncludePastOpenStatus(true);
        setBpShowInActiveTeam(showInActiveTeams);

        setBmiTeam(selectedTeam);
        setBmiIncludePastOpenStatus(true);
        setBmiShowInActiveTeam(showInActiveTeams);

        setAdultHealthTeam(selectedTeam);
        setAdultHealthIncludePastOpenStatus(true);
        setAdultHealthShowInActiveTeam(showInActiveTeams);

        setDepressionScreeningTeam(selectedTeam);
        setDepressionScreeningIncludePastOpenStatus(true);
        setDepressionScreeningShowInActiveTeam(showInActiveTeams);

        setDepressionFollowUpTeam(selectedTeam);
        setDepressionFollowUpIncludePastOpenStatus(true);
        setDepressionFollowUpShowInActiveTeam(showInActiveTeams);

        setMedicationReconciliationTeam(selectedTeam);
        setMedicationReconciliationIncludePastOpenStatus(true);
        setMedicationReconciliationShowInActiveTeam(showInActiveTeams);

        setBhDiagnosticTeam(selectedTeam);
        setBhDiagnosticIncludePastOpenStatus(true);
        setBhDiagnosticShowInActiveTeam(showInActiveTeams);

        setHgbA1cForBhDxLoadedTeam(selectedTeam);
        setHgbA1cForBhDxIncludePastOpenStatus(true);
        setHgbA1cForBhDxShowInActiveTeam(showInActiveTeams);

        setLipidForBhDxLoadedTeam(selectedTeam);
        setLipidForBhDxIncludePastOpenStatus(true);
        setLipidForBhDxShowInActiveTeam(showInActiveTeams);

        setTcTeam(selectedTeam);
        setTcShowInActiveTeams(showInActiveTeams);

        setCervicalCancerScreeningTeam(selectedTeam);
        setCervicalCancerScreeningIncludePastOpenStatus(true);
        setCervicalCancerScreeningShowInActiveTeam(showInActiveTeams);

        setColorectalCancerScreeningTeam(selectedTeam);
        setColorectalCancerScreeningIncludePastOpenStatus(true);
        setColorectalCancerScreeningShowInActiveTeam(showInActiveTeams);

        history(`/provider/metricsummary/smart-dashboard`);
    };
    return !isFetched ? (
        <Box className="loader-center">
            <CircularProgress />
        </Box>
    ) : (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container rowSpacing={3} columnSpacing={2} paddingTop={2}>
                <Grid size={12}>
                    <Button
                        variant="text"
                        sx={{ float: 'right', marginRight: '15px' }}
                        onClick={() => {
                            navigateToSmartDashboard();
                        }}
                    >
                        Smart Dashboard
                    </Button>
                </Grid>
                <Grid size={6}>
                    <HedisMetricSummary></HedisMetricSummary>
                </Grid>
                <Grid size={6}>
                    <QAMetricSummary></QAMetricSummary>
                </Grid>
                <Grid size={12}>
                    <DashRTCS></DashRTCS>
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
};

export default MetricSummary;
